import React, { useState } from 'react'
import type { TFunction } from 'i18next'
import { formatNumberSwiss } from '../../../../../../../utils/number/formatUtils'
import type { ProcessedFloor, ProcessedUnit } from '../types'
import {
  FloorHeader,
  FloorHeaderLeft,
  FloorNumber,
  FloorName,
  FloorMeta,
  TableContainer,
  UnitTable,
  TableHeader,
  TableRow,
  TableCell,
  RoomBadge,
  PriceCell,
  PriceValue,
  PriceUnit,
  ConfidenceBar,
  ConfidenceFill,
  ConfidenceDot,
  PriceRange,
  ExpandedRow,
  ExpandedAccent,
  ExpandedFields,
  ExpandedField,
  ExpandedLabel,
  ExpandedValue,
  NoPriceText,
  EmptyFloorMessage,
} from './UnitsTable.styles'

interface UnitsTableProps {
  floors: ProcessedFloor[]
  t: TFunction
}

/**
 * Renders a confidence interval bar for price predictions
 */
const PriceConfidenceBar: React.FC<{
  lowerBound: number
  upperBound: number
  price: number
  type: 'rent' | 'purchase'
}> = ({ lowerBound, upperBound, price, type }) => {
  const range = upperBound - lowerBound
  if (range <= 0) return null

  const pricePosition = ((price - lowerBound) / range) * 100

  return (
    <ConfidenceBar>
      <ConfidenceFill $color={type} />
      <ConfidenceDot
        $color={type}
        style={{ left: `${Math.min(Math.max(pricePosition, 10), 90)}%` }}
      />
    </ConfidenceBar>
  )
}

/**
 * Renders a price cell with value and confidence bar
 */
const PricePredictionCell: React.FC<{
  prediction?: { price: number; lowerBound: number; upperBound: number }
  type: 'rent' | 'purchase'
  t: TFunction
}> = ({ prediction, type, t }) => {
  if (!prediction) {
    return <NoPriceText>{t('unitDetails.common.na')}</NoPriceText>
  }

  const perMonth = type === 'rent' ? `/${t('unitDetails.table.perMonth')}` : ''

  return (
    <PriceCell>
      <PriceValue>
        {t('unitDetails.common.chf')}{' '}
        {formatNumberSwiss(Math.round(prediction.price))}
        <PriceUnit>{perMonth}</PriceUnit>
      </PriceValue>
      <PriceConfidenceBar
        lowerBound={prediction.lowerBound}
        upperBound={prediction.upperBound}
        price={prediction.price}
        type={type}
      />
      <PriceRange>
        {formatNumberSwiss(Math.round(prediction.lowerBound))} -{' '}
        {formatNumberSwiss(Math.round(prediction.upperBound))}
      </PriceRange>
    </PriceCell>
  )
}

/**
 * Renders the expanded detail row with secondary fields (read-only)
 */
const UnitExpandedDetails: React.FC<{
  unit: ProcessedUnit
  t: TFunction
}> = ({ unit, t }) => {
  return (
    <ExpandedRow>
      <ExpandedAccent />
      <ExpandedFields>
        <ExpandedField>
          <ExpandedLabel>{t('unitDetails.table.headers.fid')}</ExpandedLabel>
          <ExpandedValue>#{unit.ewid}</ExpandedValue>
        </ExpandedField>

        {unit.edid && (
          <ExpandedField>
            <ExpandedLabel>{t('unitDetails.table.headers.edid')}</ExpandedLabel>
            <ExpandedValue>{unit.edid}</ExpandedValue>
          </ExpandedField>
        )}

        <ExpandedField>
          <ExpandedLabel>
            {t('unitDetails.table.headers.adminNo')}
          </ExpandedLabel>
          <ExpandedValue>{unit.administrativeNb || '-'}</ExpandedValue>
        </ExpandedField>

        <ExpandedField>
          <ExpandedLabel>{t('unitDetails.table.headers.cYear')}</ExpandedLabel>
          <ExpandedValue>{unit.constructionDate || '-'}</ExpandedValue>
        </ExpandedField>

        <ExpandedField>
          <ExpandedLabel>{t('unitDetails.table.headers.rooms')}</ExpandedLabel>
          <ExpandedValue>{unit.roomNb || '-'}</ExpandedValue>
        </ExpandedField>

        <ExpandedField>
          <ExpandedLabel>
            {t('unitDetails.table.headers.bathrooms')}
          </ExpandedLabel>
          <ExpandedValue>{unit.bathrooms || '-'}</ExpandedValue>
        </ExpandedField>

        <ExpandedField>
          <ExpandedLabel>
            {t('unitDetails.table.headers.multiFloor')}
          </ExpandedLabel>
          <ExpandedValue>
            {unit.multipleFloor
              ? t('unitDetails.common.yes')
              : t('unitDetails.common.no')}
          </ExpandedValue>
        </ExpandedField>
      </ExpandedFields>
    </ExpandedRow>
  )
}

/**
 * Renders a floor header separator within the table
 */
const FloorHeaderRow: React.FC<{
  floor: ProcessedFloor
  t: TFunction
}> = ({ floor, t }) => (
  <FloorHeader>
    <FloorHeaderLeft>
      <FloorNumber $type={floor.type}>
        {floor.number === 0 ? 'G' : floor.number}
      </FloorNumber>
      <FloorName>{floor.name}</FloorName>
      <FloorMeta>
        {floor.totalUnits} {t('unitDetails.metrics.units').toLowerCase()}
        {floor.totalArea > 0 &&
          ` \u00B7 ${floor.totalArea} ${t('unitDetails.common.squareMeters')}`}
      </FloorMeta>
    </FloorHeaderLeft>
  </FloorHeader>
)

/**
 * Flat table with floor header separators, room badges, and price prediction cells.
 * Read-only version (no editing, no action column).
 */
export const UnitsTable: React.FC<UnitsTableProps> = ({ floors, t }) => {
  const [expandedUnits] = useState<Set<string>>(new Set())

  const totalUnits = floors.reduce((sum, floor) => sum + floor.units.length, 0)

  if (totalUnits === 0) {
    return (
      <EmptyFloorMessage>
        <p>{t('unitDetails.states.noUnitsOnFloor')}</p>
      </EmptyFloorMessage>
    )
  }

  return (
    <TableContainer>
      <UnitTable>
        <TableHeader>
          <div>{t('unitDetails.table.headers.type')}</div>
          <div>{t('unitDetails.table.headers.area')}</div>
          <div>{t('unitDetails.table.headers.monthlyRent')}</div>
          <div>{t('unitDetails.table.headers.purchasePrice')}</div>
        </TableHeader>

        {floors.map((floor) => (
          <React.Fragment key={floor.id}>
            {/* Floor separator header */}
            <FloorHeaderRow floor={floor} t={t} />

            {/* Unit rows for this floor */}
            {floor.units.map((unit) => {
              const isExpanded = expandedUnits.has(unit.id.toString())

              return (
                <React.Fragment key={unit.id.toString()}>
                  <TableRow>
                    {/* TYPE - Room badge */}
                    <TableCell>
                      <RoomBadge $roomNb={unit.roomNb || 0}>
                        {unit.typeDisplay}
                      </RoomBadge>
                    </TableCell>

                    {/* AREA */}
                    <TableCell>
                      {unit.area || 0} {t('unitDetails.common.squareMeters')}
                    </TableCell>

                    {/* MONTHLY RENT */}
                    <TableCell>
                      <PricePredictionCell
                        prediction={unit.pricePrediction?.rent}
                        type="rent"
                        t={t}
                      />
                    </TableCell>

                    {/* PURCHASE PRICE */}
                    <TableCell>
                      <PricePredictionCell
                        prediction={unit.pricePrediction?.purchase}
                        type="purchase"
                        t={t}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Expanded detail row */}
                  {isExpanded && <UnitExpandedDetails unit={unit} t={t} />}
                </React.Fragment>
              )
            })}
          </React.Fragment>
        ))}
      </UnitTable>
    </TableContainer>
  )
}
